import { type RefObject, useEffect } from "react";

// Elements that can receive keyboard focus inside a modal. `tabindex="-1"` is
// excluded so the container itself — which we make focusable only as an
// initial-focus fallback — is never treated as a Tab stop.
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Trap keyboard focus inside `containerRef` while `active` is true.
 *
 * On activate it records the currently-focused element (the trigger) and moves
 * focus into the container — its first focusable child, or the container itself
 * when there is none. While active, Tab / Shift+Tab cycle within the
 * container's focusable elements. On deactivate (or unmount) focus is restored
 * to the recorded element.
 *
 * A dependency-free trap for the hand-rolled (non-Radix) `Dialog` / `Sheet`.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    // Alias the narrowed value so its non-null type survives into the nested
    // event-handler closures below.
    const current = containerRef.current;
    if (current === null) return;
    const container: HTMLElement = current;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    function focusableItems(): HTMLElement[] {
      return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    }

    const [initialTarget] = focusableItems();
    (initialTarget ?? container).focus();

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== "Tab") return;
      const items = focusableItems();
      const first = items[0];
      const last = items[items.length - 1];
      if (first === undefined || last === undefined) {
        // Nothing focusable inside: keep focus pinned on the container.
        event.preventDefault();
        container.focus();
        return;
      }
      // Only the boundaries need redirecting; the browser moves focus between
      // interior elements on its own. The listener is container-scoped, so a
      // Tab keydown only reaches here while focus is already inside the modal.
      const focused = document.activeElement;
      if (event.shiftKey && focused === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && focused === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener("keydown", onKeyDown);
    return () => {
      container.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus();
    };
  }, [active, containerRef]);
}
